import { useCallback } from "react"
import type { DateTime } from "luxon"

import { isMultiDay } from "../../lib/time"
import { useAppLocalization } from "../../modules/localization/components/AppLocalizationProvider"
import { useDateTime } from "../../modules/localization/useDateTime"

// All fields optional: the Postman examples show some meeting endpoints omit meetingTimeAsText,
// and the date times are only trusted after they parse.
type MeetingTimeDetails = Readonly<{
    startDateTime?: string
    endDateTime?: string
    meetingTimeAsText?: string
}>

/**
 * Localized meeting time label ("Today at 08:15 - 09:00") built from the meeting's start and end
 * date times. A meeting spanning several days gets the day next to each time
 * ("Today at 12:00 - Tomorrow at 12:00"). The API's meetingTimeAsText is English-only, so it is
 * used only as the fallback when the date times are missing or unparsable.
 */
export function useMeetingTimeLabel() {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    return useCallback(
        (details: MeetingTimeDetails): string => {
            const start = dateTimeFactory.fromApiOrUndefined(details.startDateTime)
            const end = dateTimeFactory.fromApiOrUndefined(details.endDateTime)
            if (!start?.isValid || !end?.isValid) return details.meetingTimeAsText ?? ""

            // Calendar dates compared as ISO strings, like meetingDayGroups does: the meeting is in
            // the API zone and now() in the browser zone, so a DateTime diff could come out fractional.
            const now = dateTimeFactory.now()
            const withDay = (dateTime: DateTime, period: string) => {
                const iso = dateTime.toISODate()
                if (iso === now.toISODate()) return l10n.getString("meeting-time-label-today", { period })
                if (iso === now.plus({ days: 1 }).toISODate())
                    return l10n.getString("meeting-time-label-tomorrow", { period })
                if (iso === now.minus({ days: 1 }).toISODate())
                    return l10n.getString("meeting-time-label-yesterday", { period })
                return l10n.getString("meeting-time-label-date", {
                    date: dateTimeFactory.formatLongDate(dateTime),
                    period,
                })
            }

            const startTime = dateTimeFactory.formatTime(start)
            const endTime = dateTimeFactory.formatTime(end)
            if (!isMultiDay(start, end)) return withDay(start, `${startTime} - ${endTime}`)
            return `${withDay(start, startTime)} - ${withDay(end, endTime)}`
        },
        [dateTimeFactory, l10n],
    )
}
